import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import Skeleton from "./Skeleton";
import EmptyState from "./EmptyState";

/**
 * Standard drawer body states. Each keeps the drawer's header and footer at their
 * normal dimensions, so switching between states never shifts the layout.
 */

export function RightSheetLoading() {
  return (
    <div
      data-testid="right-sheet-loading"
      role="status"
      aria-live="polite"
      className="rounded-md border border-card-bd divide-y divide-border overflow-hidden"
    >
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} height={56} className="rounded-none" />
      ))}
    </div>
  );
}

export function RightSheetError({ message, onRetry }) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center gap-2 py-16 text-center">
      <div
        className="mb-2 flex items-center justify-center rounded-full bg-danger-soft text-danger"
        style={{ width: 56, height: 56 }}
      >
        <FontAwesomeIcon
          icon={icon({ name: "triangle-exclamation", style: "solid" })}
          className="text-2xl"
        />
      </div>
      <p className="text-base font-semibold text-text">Something went wrong</p>
      <p className="max-w-sm text-sm text-text-2">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 inline-flex items-center gap-1.5 rounded-ctrl border border-border border-solid box-border bg-card px-3 py-1.5 text-[13px] font-medium text-text hover:bg-surface-2 cursor-pointer"
        >
          Try again
        </button>
      )}
    </div>
  );
}

RightSheetError.propTypes = {
  message: PropTypes.node,
  onRetry: PropTypes.func,
};

export function RightSheetEmpty({ empty }) {
  // Anything that isn't a plain {icon,title,message,action} descriptor — an element,
  // a string, an array of nodes — renders as-is.
  if (React.isValidElement(empty)) return empty;
  if (typeof empty !== "object" || empty === null || Array.isArray(empty)) return empty ?? null;
  const { icon: emptyIcon, title, message, action } = empty;
  return <EmptyState icon={emptyIcon} title={title} message={message} action={action} />;
}

RightSheetEmpty.propTypes = {
  empty: PropTypes.oneOfType([PropTypes.node, PropTypes.object]),
};
